<script setup lang="ts">
	import BreadCrumb from '@/components/BreadCrumb.vue'
	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import useInternalUsers from '@/internal/modules/users/composables/useInternalUsers'
	import useCompanyUser from '@/modules/companies/composables/useCompanyUser'
	import UserForm from '@/modules/users/components/UserForm.vue'
	import useUser from '@/modules/users/composables/useUser'
	import useUsersCompany from '@/modules/users/composables/useUsersCompany'
	import { useQueryClient } from '@tanstack/vue-query'
	import { useVuelidate } from '@vuelidate/core'
	import '@vuepic/vue-datepicker/dist/main.css'
	import { FormHTMLAttributes, onBeforeMount, onMounted, ref, watch } from 'vue'
	import { useRouter } from 'vue-router'
	import { useToast } from 'vue-toastification'

	const { push } = useRouter()
	const toast = useToast()

	const queryClient = useQueryClient()
	const { createForm, resetCreateForm, rulesUserValidation } = useUser({
		immediate: false,
	})

	const idForm = ref<HTMLFormElement>()

	const { abilities, hasPermissionUser, redirectTo404 } = useHasPermissionUser()

	const { createUserMutationAsync } = useCompanyUser()
	const { createUserMutationAsync: createUserIntenalMutationAsync } =
		useInternalUsers({
			immediate: false,
		})
	const { filters, refetch } = useUsersCompany(
		{},
		{
			immediate: false,
		}
	)

	const pages = [
		{ name: 'Usuarios', to: { name: 'users.index' }, current: true },
		{ name: 'Crear usuario', to: { name: 'users.create.internal' } },
	]

	const visitUserList = () => {
		push({ name: 'users.index' })
	}

	const v$ = useVuelidate(rulesUserValidation, createForm)

	const handleSubmit = async () => {
		for (const field of v$.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		if (!createForm.value.companyId) {
			const user = await createUserIntenalMutationAsync({
				birthDate: createForm.value.birthDate,
				email: createForm.value.email ?? '',
				gender: createForm.value.gender ?? '',
				lastname: createForm.value.lastname ?? '',
				name: createForm.value.name ?? '',
				roleId: createForm.value.roleId!,
			})

			if (user) {
				visitUserList()
				resetCreateForm()
			}

			return
		}

		const user = await createUserMutationAsync({
			name: createForm.value.name,
			secondName: createForm.value.secondName ?? '',
			lastname: createForm.value.lastname,
			secondLastname: createForm.value.secondLastname ?? '',
			birthDate: createForm.value.birthDate,
			gender: createForm.value.gender,
			companyId: createForm.value.companyId ?? '',
			roleId: createForm.value.roleId ?? '',
			email: createForm.value.email,
			companyGroupId: Number(createForm.value.companyGroupId) ?? undefined,
			jobTitle: '',
			haveAccess: createForm.value.haveAccess,
			departmentId: createForm.value.departmentId ?? 0,
		})

		if (user) {
			toast.success('Usuario creado correctamente')

			filters.value.company = createForm.value.companyId
			refetch()

			visitUserList()
			resetCreateForm()

			await queryClient.invalidateQueries(['usersCompany'])

			return
		}
	}

	watch(
		abilities,
		() => {
			redirectTo404('create-internal', 'App\\Models\\User')
		},
		{
			immediate: true,
		}
	)
</script>

<template>
	<BreadCrumb :pages="pages"></BreadCrumb>

	<section class="mx-auto my-8 grid h-full max-w-4xl gap-4 px-4">
		<UserForm
			@cancel-form="visitUserList"
			@send-form="handleSubmit"
			:not-have-default-value-role="true"
			:company-selected="undefined"
		/>
	</section>
</template>
